import React, { ReactElement, ReactNode, useCallback } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

import { CMD_OR_CTRL_PLUS_ENTER } from '../../../../utils/keyboardHotkeys'
import { Button } from '../../../Button'
import { ButtonsGroup } from '../../../ButtonsGroup'
import { Text } from '../../../Text'
import { useModal } from '../../context/hooks/useModal'
import { Modal, ModalProps } from '../../Modal'

export interface ModalConfirmationProps extends Omit<ModalProps, 'closable' | 'children'> {
  cancelLabel?: string
  danger?: boolean
  message?: ReactNode
  okButtonTestId?: string
  okLabel?: string
  okLoading?: boolean
  onCancel?: () => void
  onClose?: () => void
  onOk?: () => void
  onOpen?: () => void
  title?: ReactNode
}

export const ModalConfirmation = ({
  cancelLabel,
  danger,
  id,
  message,
  okButtonTestId,
  okLabel,
  okLoading,
  onCancel,
  onClose,
  onOk,
  onOpen,
  title,
  ...rest
}: ModalConfirmationProps): ReactElement => {
  const { close, isOpen } = useModal(id, { onClose, onOpen })

  const handleCancelClick = useCallback(() => {
    onCancel?.()
    close()
  }, [close, onCancel])

  const handleOkClick = useCallback(() => {
    onOk?.()
  }, [onOk])

  useHotkeys(
    CMD_OR_CTRL_PLUS_ENTER,
    handleOkClick,
    { enabled: isOpen, enableOnTags: ['INPUT', 'SELECT', 'TEXTAREA'] },
    [isOpen, handleOkClick],
  )

  return (
    <Modal id={id} size="s" closable {...rest}>
      {title && <Modal.Header title={title} />}
      {message && (
        <Modal.Body>
          {React.isValidElement(message) ? message : <Text colorVariant="secondary">{message}</Text>}
        </Modal.Body>
      )}
      <Modal.Footer>
        <ButtonsGroup>
          {cancelLabel && (
            <Button variant="text" onClick={handleCancelClick}>
              {cancelLabel}
            </Button>
          )}
          {okLabel && (
            <Button
              data-testid={okButtonTestId}
              onClick={handleOkClick}
              color={danger ? 'danger' : undefined}
              loading={okLoading}
            >
              {okLabel}
            </Button>
          )}
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
